import { Grid, CheckCircle, X, Palette, Eye, Fingerprint, Users, MessageCircle, AlertTriangle, Layers, Activity, Scale, Baby, Briefcase } from "lucide-react";
import { formatText } from "../../DanvinciHelpers";

export const Visual = ({authenticity, access}) => {

    // --- DATA MINING ---
    // 1. Face Detection / Human Centricity
    const viralFactors = access('viral_potential_analysis.viral_factors_present') || [];
    
    // FIXED: Regex search to find "face" OR "faces" so we don't miss count of 1
    const faceFactor = viralFactors.find(f => /face(s)? detected/i.test(f)) || "";
    const faceCount = faceFactor ? parseInt(faceFactor.match(/(\d+)/)?.[0] || 0) : 0;

    // 2. Rhetoric & Function
    const rhetoric = access('visual_rhetoric.rhetorical_appeals') || access('visual_rhetoric_analysis.rhetorical_appeals') || [];
    const primaryRhetoric = rhetoric[0] || "Neutral";
    const functionType = access('visual_rhetoric.communicative_function') || access('visual_rhetoric_analysis.communicative_function') || "General Messaging";

    // 3. Color Rationale
    const colorRationale = access('color_resonance_analysis.rationale') || "Standard palette detected.";

    // 4. Generic Markers
    const genericMarkers = access('authenticity_differentiation.generic_markers_detected') || 0;

    // 5. Demographics (Compact)
    const demographics = access('demographicRepresentationAudit') || {};
    const ageDist = demographics.age_representation?.distribution || {};
    const lifestyleDist = demographics.lifestyle_representation?.distribution || {};
    const inclusivityScore = (demographics.accessibility_representation?.inclusivity_score || 0) * 100;

    // Tooltip Component
    const Tooltip = ({ text }) => (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-48 p-3 bg-slate-950/95 border border-slate-700 text-slate-300 text-[10px] leading-relaxed rounded-lg shadow-xl opacity-0 transition-opacity duration-0 delay-0 group-hover:opacity-100 group-hover:duration-300 group-hover:delay-1000 pointer-events-none z-50">
            {text}
            <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-950 border-r border-b border-slate-700 rotate-45"></div>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in-slide-in-from-bottom-2 duration-300 pb-10">
            
            {/* ROW 1: COMPOSITION & HUMAN CENTRICITY */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Architecture */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center">
                        <Grid size={14} className="mr-2 text-purple-400" /> Composition DNA
                    </h3>
                    
                    <div className="space-y-3">
                        <div className="flex justify-between items-center pb-2 border-b border-slate-700/30">
                            <span className="text-sm text-slate-400">Archetype</span>
                            <span className="text-sm font-bold text-white">
                                {formatText(access('composition_archetype') || "Unstructured")}
                            </span>
                        </div>
                        
                        <div className="flex justify-between items-center pb-2 border-b border-slate-700/30">
                            <span className="text-sm text-slate-400">Balance</span>
                            <span className="text-sm font-bold text-white">
                                {formatText(access('visual_balance_type') || "Neutral")}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-1">
                            <div className="bg-slate-900/50 p-2 rounded flex items-center justify-between">
                                <span className="text-[9px] text-slate-500">Rule of Thirds</span>
                                {access('follows_rule_of_thirds') ? 
                                    <CheckCircle size={14} className="text-emerald-400"/> : 
                                    <X size={14} className="text-slate-600"/>}
                            </div>
                            <div className="bg-slate-900/50 p-2 rounded flex items-center justify-between">
                                <span className="text-[9px] text-slate-500">Golden Ratio</span>
                                {access('follows_golden_ratio') ? 
                                    <CheckCircle size={14} className="text-emerald-400"/> : 
                                    <X size={14} className="text-slate-600"/>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Human Elements */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50 flex flex-col justify-between">
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center">
                        <Users size={14} className="mr-2 text-blue-400" /> Human Centricity
                    </h3>

                    <div className="flex items-end gap-2 mb-4">
                        <span className="text-4xl font-black text-white">{faceCount}</span>
                        {/* FIXED: Conditional Label Logic */}
                        <span className="text-xs text-slate-400 mb-1.5 font-medium">
                            {faceCount === 1 ? 'Face Detected' : 'Faces Detected'}
                        </span>
                    </div>

                    <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
                        <span className="text-[10px] text-slate-500 uppercase block mb-1">Crowd Dynamics</span>
                        <div className="flex items-center gap-2">
                            <div className={`h-2 w-full rounded-full overflow-hidden ${
                                faceCount > 5 ? 'bg-blue-900/30' : 'bg-slate-700/30'
                            }`}>
                                <div 
                                    className={`h-full rounded-full transition-all duration-1000 ${
                                        faceCount > 5 ? 'bg-blue-400' : 'bg-slate-500'
                                    }`}
                                    style={{ width: `${Math.min(faceCount * 10, 100)}%` }}
                                />
                            </div>
                            <span className="text-xs font-bold text-blue-200">
                                {faceCount > 5 ? "High" : faceCount > 1 ? "Moderate" : "Low"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ROW 2: VISUAL RHETORIC */}
            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center">
                    <MessageCircle size={14} className="mr-2 text-pink-400"/> Visual Rhetoric
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-900/50 rounded border border-slate-700/30 group relative cursor-help">
                        <span className="text-[10px] text-slate-500 uppercase block mb-1">Primary Appeal</span>
                        <span className="text-lg font-bold text-pink-300 capitalize">{primaryRhetoric}</span>
                        <Tooltip text="The dominant mode of persuasion: Ethos (Credibility), Pathos (Emotion), or Logos (Logic)." />
                    </div>
                    
                    <div className="p-3 bg-slate-900/50 rounded border border-slate-700/30 group relative cursor-help">
                        <span className="text-[10px] text-slate-500 uppercase block mb-1">Communicative Goal</span>
                        <span className="text-lg font-bold text-white capitalize">{functionType}</span>
                        <Tooltip text="The functional objective of the visual composition (e.g., to Inspire, Inform, or Warn)." />
                    </div>
                </div>
            </div>

            {/* ROW 3: DEMOGRAPHIC & LIFESTYLE */}
            {(Object.keys(ageDist).length > 0 || Object.keys(lifestyleDist).length > 0) && (
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center">
                            <Activity size={14} className="mr-2 text-emerald-400" /> Demographic Signal
                        </h3>
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-900/50 border border-slate-700 rounded">
                            <Scale size={10} className="text-slate-400" />
                            <span className="text-[10px] font-bold text-white">{inclusivityScore.toFixed(0)}% Inclusivity</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
                            <span className="text-[10px] text-slate-500 uppercase block mb-2 flex items-center gap-1.5">
                                <Baby size={10}/> Age Groups
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(ageDist).map(([key, count], i) => (
                                    count > 0 && (
                                        <span key={i} className="px-1.5 py-0.5 bg-slate-800 border border-slate-600 rounded text-[10px] text-slate-300 flex items-center gap-1">
                                            {formatText(key)} <span className="text-emerald-400 font-bold">{count}</span>
                                        </span>
                                    )
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
                            <span className="text-[10px] text-slate-500 uppercase block mb-2 flex items-center gap-1.5">
                                <Briefcase size={10}/> Lifestyle
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(lifestyleDist).map(([key, count], i) => (
                                    count > 0 && (
                                        <span key={i} className="px-1.5 py-0.5 bg-slate-800 border border-slate-600 rounded text-[10px] text-slate-300 flex items-center gap-1">
                                            {formatText(key)} <span className="text-blue-400 font-bold">{count}</span>
                                        </span>
                                    )
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ROW 4: COLOR STRATEGY */}
            {access('color_resonance_analysis.image_palette_hex') && (
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center">
                        <Palette size={14} className="mr-2 text-amber-400"/> Color Strategy
                    </h3>
                    
                    <div className="flex h-12 rounded-lg overflow-hidden border border-slate-600 mb-4">
                        {access('color_resonance_analysis.image_palette_hex').map((hex, i) => (
                            <div 
                                key={i}
                                className="flex-1 cursor-pointer relative group"
                                style={{ backgroundColor: hex }}
                                title={hex}
                            >
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                                    <span className="text-white text-[10px] font-mono">{hex}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="p-3 bg-slate-900/30 rounded border border-slate-700/30 flex items-start gap-3">
                        <Layers size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-slate-300 italic leading-relaxed">
                            "{colorRationale}"
                        </p>
                    </div>
                </div>
            )}

            {/* ROW 5: ATTENTION & RISK */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                        <Eye size={14} className="mr-2 text-cyan-400"/> Attention
                    </h3>
                    <div className="flex justify-between items-end">
                        <div>
                            <span className="text-[10px] text-slate-500 block">Capture Score</span>
                            <span className="text-xl font-bold text-white">
                                {Math.round((access('attention_capture_analysis.attention_capture_score') || 0) * 100)}%
                            </span>
                        </div>
                        <div className="text-right">
                            <span className="text-[10px] text-slate-500 block">Est. Dwell</span>
                            <span className="text-xl font-bold text-cyan-400">
                                {access('attention_capture_analysis.estimated_dwell_time_seconds', 0).toFixed(1)}s
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                        <AlertTriangle size={14} className={`mr-2 ${genericMarkers > 0 ? 'text-amber-400' : 'text-slate-600'}`}/> Risk Factors
                    </h3>
                    <div className="flex justify-between items-end">
                        <div>
                            <span className="text-[10px] text-slate-500 block">Generic Markers</span>
                            <span className={`text-xl font-bold ${genericMarkers > 0 ? 'text-amber-400' : 'text-slate-400'}`}>
                                {genericMarkers}
                            </span>
                        </div>
                        <div className="text-right">
                            <span className="text-[10px] text-slate-500 block">Authenticity</span>
                            <span className={`text-xl font-bold ${
                                access('authenticity_differentiation.category') === 'HIGHLY_AUTHENTIC' ? 'text-emerald-400' : 'text-yellow-400'
                            }`}>
                                {Math.round((access('authenticity_differentiation.authenticity_score') || 0) * 100)}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};